import React, { useEffect, useState } from "react";
import {
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    CircularProgress,
    TextField,
} from "@mui/material";
import { GridSortModel, GridFilterModel } from "@mui/x-data-grid";
import { DataGrid, GridColDef } from "@mui/x-data-grid";
import Layout from "../components/Layout";
import axiosExt from '../axios';
import { API_BASE_URL } from "../config.ts";
import { Invoice, Customer } from "../types";
import { useTranslation } from "react-i18next";

import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs, { Dayjs } from "dayjs";

import Autocomplete from "@mui/material/Autocomplete";

type LinkedOrder = {
    id: string;
    internalNumber: string;
};

const InvoiceListPage: React.FC = () => {
    const { t } = useTranslation();

    const [invoices, setInvoices] = useState<Invoice[]>([]);
    const [customers, setCustomers] = useState<Customer[]>([]);
    const [loading, setLoading] = useState<boolean>(false);
    //const [open, setOpen] = useState<boolean>(false);
    //const [selectedInvoice, setSelectedInvoice] = useState<Invoice | null>(null);
    const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
    const [itemToDelete, setItemToDelete] = useState<string | null>(null);

    const [linkedOrdersDialogOpen, setLinkedOrdersDialogOpen] = useState(false);
    const [linkedOrders, setLinkedOrders] = useState<LinkedOrder[]>([]);

    const [sortModel, setSortModel] = useState<GridSortModel>([]);
    const [filterModel, setFilterModel] = useState<GridFilterModel>({ items: [] });

    const [customerFilter, setCustomerFilter] = useState("");
    const [invoiceStartDate, setInvoiceStartDate] = useState<Dayjs | null>(null);
    const [invoiceEndDate, setInvoiceEndDate] = useState<Dayjs | null>(null);
    const [invoiceNumberFilter, setInvoiceNumberFilter] = useState("");
    //const [filtersInitialized, setFiltersInitialized] = useState(false);
    //const hasActiveFilters = !!customerFilter || invoiceStartDate !== null || invoiceEndDate !== null || !!invoiceNumberFilter; 

    const [reloadDialogOpen, setReloadDialogOpen] = useState(false);
    const [fromDate, setFromDate] = useState<Date | null>(null);
    const [syncing, setSyncing] = useState(false);

    //const invoiceFilterStorageKey = "invoiceFilters";
    //const [fromFilter, setFromFilter] = useState("");

    const savedSortKey = "sortInvoiceList";
    const savedFilterKey = "filterInvoiceList";
    const savedTopFilterKey = "filterTopInvoiceList";

    const canLoadInvoices = () => {
        // A) customer selected
        if (customerFilter) return true;

        // C) invoice number at least 3 chars
        if (invoiceNumberFilter.trim().length >= 3) return true;

        // B) date range valid
        if (invoiceStartDate && invoiceEndDate) {
            const diff = invoiceEndDate.diff(invoiceStartDate, "day");
            if (diff >= 0 && diff <= 90) return true;
        }

        return false;
    };

    useEffect(() => {
        const stored = localStorage.getItem(savedTopFilterKey);
        if (stored) {
            try {
                const parsed = JSON.parse(stored);
                if (parsed.customer) setCustomerFilter(parsed.customer);
                if (parsed.startDate) setInvoiceStartDate(dayjs(parsed.startDate));
                if (parsed.endDate) setInvoiceEndDate(dayjs(parsed.endDate));
                if (parsed.invoiceNumberFilter) setInvoiceNumberFilter(parsed.invoiceNumberFilter);
            } catch (e) {
                console.warn("Failed to parse invoice filters:", e);
            }
        } else {
            const now = dayjs();
            setInvoiceStartDate(now.subtract(90, "day").startOf("day"));
            setInvoiceEndDate(now.endOf("day"));
        }
        //setFiltersInitialized(true);

        const savedSortModel = JSON.parse(localStorage.getItem(savedSortKey) ?? "[]");
        const savedFilterModel = (() => {
            try {
                const parsed = JSON.parse(localStorage.getItem(savedFilterKey) ?? "{}");
                return parsed && parsed.items ? parsed : { items: [] };
            } catch (error) {
                console.error("Error parsing savedFilterKey:", error);
                return { items: [] }; // Fallback
            }
        })();
        setSortModel(savedSortModel);
        setFilterModel(savedFilterModel);
        fetchCustomers();
        if (canLoadInvoices()) {
            fetchInvoices();
        }
    }, []);

    //useEffect(() => {
    //    if (!isFiltersInitialized) return;

    //    // Only fetch if at least one filter is active
    //    const hasActiveFilters =
    //        !!fromFilter || (subjectFilter?.trim().length >= 3) || !!receivedStart || !!receivedEnd;

    //    if (hasActiveFilters) {
    //        fetchInvoices();
    //    }
    //}, [isFiltersInitialized, fromFilter, subjectFilter, receivedStart, receivedEnd]);

    //useEffect(() => {
    //    if (!filtersInitialized) return;

    //    const hasFilters = !!customerFilter || invoiceStartDate || invoiceEndDate;
    //    if (hasFilters) fetchInvoices();
    //}, [filtersInitialized, customerFilter, invoiceStartDate, invoiceEndDate]);

    const fetchCustomers = async () => {
        //setLoading(true);
        const token = localStorage.getItem("token");

        try {
            const response = await axiosExt.get<Customer[]>(`${API_BASE_URL}/customers`, {
                method: "GET",
                headers: {
                    "Content-Type": "application/json",
                    "Authorization": `Bearer ${token}`,
                },
            }

            );

            if (response.status === 401) {
                throw new Error("Unauthorized. Please log in again.");
            }
            //const sortedCustomers = [...response.data].sort((a, b) =>
            //    a.name.localeCompare(b.name)
            //);
            // filter + sort
            const filteredSortedCustomers = response.data
                .filter(c => c.accountingCustomerNumber && c.accountingCustomerNumber.trim() !== "")
                .sort((a, b) => a.name.localeCompare(b.name));

            setCustomers(filteredSortedCustomers);
        } catch (error) {
            console.error("Error fetching customers:", error);
        }
        finally {
            //setLoading(false);
        }
    };

    const fetchInvoices = async () => {
        if (!canLoadInvoices()) {
            alert(t("Please select a customer OR a valid date range (max 90 days) OR enter at least 3 characters of the invoice number."));
            return;
        }

        setLoading(true);
        try {
            const token = localStorage.getItem("token");
            setSyncing(true);

            localStorage.setItem(savedTopFilterKey, JSON.stringify({
                customer: customerFilter,
                startDate: invoiceStartDate?.toISOString() ?? null,
                endDate: invoiceEndDate?.toISOString() ?? null,
                invoiceNumberFilter: invoiceNumberFilter,
            }));

            const params: {
                accountingCustomerNumber?: string;
                invoiceDateStart?: string;
                invoiceDateEnd?: string;
                invoiceNumberFilter?: string;
            } = {};

            if (customerFilter) params.accountingCustomerNumber = customerFilter;
            if (invoiceStartDate) params.invoiceDateStart = invoiceStartDate.toISOString();
            if (invoiceEndDate) params.invoiceDateEnd = invoiceEndDate.endOf("day").toISOString();
            if (invoiceNumberFilter) params.invoiceNumberFilter = invoiceNumberFilter;
            const response = await axiosExt.get(`${API_BASE_URL}/Invoices?includeContent=false`, {
                headers: { Authorization: `Bearer ${token}` },
                params,
            });

            setInvoices(response.data);
        } catch (error: unknown) {
            if (error instanceof Error) {
                console.error("Caught an error:", error.message);
            }
            else {
                console.error("An unknown error occurred");
            }

        } finally {
            setLoading(false);
            setSyncing(false);
        }
    };

    const handleViewLinkedOrders = async (invoiceId: string) => {
        try {
            const token = localStorage.getItem("token");
            const response = await axiosExt.get(`${API_BASE_URL}/invoices/${invoiceId}/linked-orders`, {
                headers: { Authorization: `Bearer ${token}` },
            });
            setLinkedOrders(response.data || []);
            setLinkedOrdersDialogOpen(true);
        } catch (error) {
            console.error("Failed to fetch linked orders:", error);
            setLinkedOrders([]);
            setLinkedOrdersDialogOpen(true);
        }
    };

    const handleViewInvoice = async (invoiceId: string) => {
        try {
            const token = localStorage.getItem("token");
            const response = await axiosExt.get(`${API_BASE_URL}/invoices/${invoiceId}/pdf`, {
                headers: { Authorization: `Bearer ${token}` },
                responseType: 'blob',
            });

            //const blob = new Blob([response.data], { type: 'application/pdf' });
            //const url = window.URL.createObjectURL(blob);
            //window.open(url, "_blank");

            const blob = new Blob([response.data], { type: 'application/pdf' });
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `${invoiceId}.pdf`;
            document.body.appendChild(a);
            a.click();
            a.remove();
            window.URL.revokeObjectURL(url);
        } catch (error) {
            console.error("Failed to fetch PDF invoice:", error);
            alert("Could not load invoice PDF.");
        }
    };


    // Save sortModel changes to localStorage
    const handleSortModelChange = (newModel: GridSortModel) => {
        setSortModel(newModel);
        localStorage.setItem(savedSortKey, JSON.stringify(newModel));
    };

    // Save filterModel changes to localStorage
    const handleFilterModelChange = (newModel: GridFilterModel) => {
        setFilterModel(newModel);
        localStorage.setItem(savedFilterKey, JSON.stringify(newModel));
    };

    const handleSyncInvoices = async () => {
        try {
            if (!fromDate) return;
            const token = localStorage.getItem("token");
            const formattedDate = fromDate.toISOString().split("T")[0];
            await axiosExt.post(`${API_BASE_URL}/invoices/sync-invoices?fromDate=${formattedDate}`, null, {
                headers: { Authorization: `Bearer ${token}` },
            });
            fetchInvoices();
        } catch (error) {
            console.error("Error syncing paid invoices:", error);
        } finally {
            setReloadDialogOpen(false);
        }
    };

    /*const handleView = (invoice: Invoice) => {
        setSelectedInvoice(invoice);
        setOpen(true);
    };*/

    //const handleView = async (invoice: Invoice) => {
    //    setLoading(true);
    //    try {
    //        const token = localStorage.getItem("token");
    //        const response = await axiosExt.get(`${API_BASE_URL}/Invoices/${invoice.id}`, {
    //            headers: { Authorization: `Bearer ${token}` },
    //        });
    //        setSelectedInvoice(response.data);
    //        setOpen(true);
    //    } catch (error) {
    //        console.error("Error fetching invoice details:", error);
    //    } finally {
    //        setLoading(false);
    //    }
    //};


    const handleDelete = async (id: string) => {
        //const token = localStorage.getItem("token");

        try {
            setItemToDelete(id);
            setConfirmDeleteOpen(true);
            /*await axiosExt.delete(`${API_BASE_URL}/invoices/${id}`, {
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            });
            setInvoices(invoices.filter((c) => c.id !== id));*/
        } catch (error) {
            console.error("Error deleting invoice:", error);
        }
    };

    const handleConfirmDelete = async () => {
        if (itemToDelete === null) return;
        try {
            const token = localStorage.getItem("token");
            await axiosExt.delete(`${API_BASE_URL}/invoices/${itemToDelete}`, {
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            });
            setInvoices(invoices.filter((c) => c.id !== itemToDelete));
        } catch (error) {
            console.error("Error deleting:", error);
        } finally {
            setConfirmDeleteOpen(false);
            setItemToDelete(null);
        }
    };

    const handleCancelDelete = () => {
        setConfirmDeleteOpen(false);
        setItemToDelete(null);
    };

    //const handleClose = () => {
    //    setOpen(false);
    //    setSelectedInvoice(null);
    //};

    const invoiceColumns: GridColDef[] = [
        {
            field: "isPaid", headerName: t("Is paid"), width: 150,
            renderCell: (params) => (params.value ? t("yes") : t("no")),
        },
        { field: "accountingInvoiceNumber", headerName: t("Invoice Number"), width: 150 },
        { field: "accountingCustomerNumber", headerName: t("Customer Number"), width: 150 },
        {
            field: "customerName",
            headerName: t("Customer"),
            width: 200,
            valueGetter: (params) => {
                return params;
            },
            renderCell: (params) => {
                const invoice = params.row as Invoice;
                const customer = customers.find(c => c.accountingCustomerNumber === invoice.accountingCustomerNumber);
                return customer?.name || "";
            }
        },
        { field: "amount", headerName: t("Amount"), width: 150 },
        {
        field: "invoiceDate",
            headerName: t("Invoice Date"),
            width: 180,
            renderCell: (params) => {
                if (!params.value) return ""; // Handle empty values

                const utcDate = new Date(params.value + "Z");
                // Convert UTC to the user's local time zone
                const localDate = new Intl.DateTimeFormat(undefined, {
                    dateStyle: "medium",
                    timeStyle: "short",
                    timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone, // Browser's time zone
                }).format(utcDate);
                return localDate;
            }
        },
        {
            field: "actions",
            headerName: t("Actions"),
            width: 340,
            renderCell: (params) => {
                const invoice = params.row as Invoice;

                return (
                    <>
                        <Button
                            variant="contained"
                            color="primary"
                            onClick={() => handleViewInvoice(invoice.id ?? "")}
                            style={{ marginRight: "10px" }}
                        >
                            {t("View")}
                        </Button>
                        <Button
                            variant="contained"
                            color="secondary"
                            onClick={() => handleViewLinkedOrders(invoice?.id || "")}
                            style={{ marginRight: "10px" }}
                        >
                            {t("Orders")}
                        </Button>
                        <Button
                            variant="contained"
                            color="error"
                            onClick={() => handleDelete(invoice?.id || "")}
                            style={{ marginRight: "10px" }}
                        >
                            {t("Delete")}
                        </Button>

                    </>
                );
            }

        },
    ];

    return (
        <Layout>
            <div>
                <div style={{ width: "100%", display: "flex" }}>
                    <h2 style={{ marginRight: "1rem" }}>{t("Invoices")}</h2>
                    <Button
                        variant="outlined"
                        onClick={() => setReloadDialogOpen(true)}
                        style={{ marginBottom: "20px", marginTop: "20px", marginLeft: "20px" }}
                        disabled={loading}
                    >
                        {t("Reload from accounting")}
                    </Button>
                </div>
            </div>

            {loading ? (
                <CircularProgress />
            ) : (

                    <Box sx={{ height: "100%", width: "100%" }}>
                        <Box sx={{ display: "flex", gap: 2, mb: 2 }}>
                            <Autocomplete
                                options={customers}
                                getOptionLabel={(option) => option.name}
                                value={customers.find(c => c.accountingCustomerNumber === customerFilter) || null}
                                onChange={(_, newValue) => {
                                    setCustomerFilter(newValue?.accountingCustomerNumber || "");
                                }}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        label={t("Customer")}
                                        variant="outlined"
                                        size="small"
                                    />
                                )}
                                sx={{ minWidth: 250 }}
                            />
                            <LocalizationProvider dateAdapter={AdapterDayjs}>
                                <DatePicker
                                    label="Invoice Start Date"
                                    value={invoiceStartDate}
                                    onChange={(newValue) => setInvoiceStartDate(newValue)}
                                />
                                <DatePicker
                                    label="Invoice End Date"
                                    value={invoiceEndDate}
                                    onChange={(newValue) => setInvoiceEndDate(newValue)}
                                />
                            </LocalizationProvider>

                            <TextField
                                label={t("Invoice Number")}
                                value={invoiceNumberFilter}
                                onChange={(e) => setInvoiceNumberFilter(e.target.value)} 
                                variant="outlined"
                                size="small"
                            />
                            <Button
                                variant="contained"
                                onClick={fetchInvoices}
                                disabled={!canLoadInvoices}
                                sx={{ alignSelf: "center", height: "40px" }}
                            >
                                {t("Apply Filters")}
                            </Button>
                        </Box>
                        {!canLoadInvoices && (
                            <Box sx={{ mt: 2 }}>
                                <span style={{ color: "red" }}>
                                    {t("Please select a customer OR a valid date range (max 90 days) OR enter at least 3 characters of the invoice number.")}
                                </span>
                            </Box>
                        )}
                    <DataGrid
                            rows={invoices}
                            columns={invoiceColumns}
                            sortModel={sortModel}
                            onSortModelChange={handleSortModelChange}
                            filterModel={filterModel}
                            onFilterModelChange={handleFilterModelChange}
                            //getRowId={(row: Invoice) => row.id}
                            initialState={{
                                pagination: {
                                    paginationModel: { pageSize: 100, page: 0 },
                                },
                            }}
                            pageSizeOptions={[5, 10, 20, 50, 100, 200]}
                        />
                </Box>
            )}

            <Dialog open={confirmDeleteOpen} onClose={handleCancelDelete}>
                <DialogTitle>{t("Confirm Deletion")}</DialogTitle>
                <DialogContent>{t("Are you sure you want to delete this record?")}</DialogContent>
                <DialogActions>
                    <Button onClick={handleCancelDelete} color="secondary">{t("no")}</Button>
                    <Button onClick={handleConfirmDelete} color="error">{t("yes")}</Button>
                </DialogActions>
            </Dialog>

            <Dialog open={reloadDialogOpen} onClose={() => setReloadDialogOpen(false)}>
                <DialogTitle>{t("Reload")}</DialogTitle>
                <DialogContent>
                    <LocalizationProvider dateAdapter={AdapterDateFns}>
                        <DatePicker
                            label={t("From date")}
                            value={fromDate}
                            onChange={(newValue) => setFromDate(newValue)}
                            slotProps={{ textField: { fullWidth: true, sx: { mt: 2 } } }}
                        />
                    </LocalizationProvider>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setReloadDialogOpen(false)}>{t("Cancel")}</Button>
                    {/*<Button onClick={handleSyncPaidInvoices} disabled={!fromDate}>{t("Reload")}</Button>*/}
                    <Button onClick={handleSyncInvoices} disabled={!fromDate || syncing}>
                        {syncing ? <CircularProgress size={20} /> : t("Reload")}
                    </Button>   
                </DialogActions>
            </Dialog>

            <Dialog open={linkedOrdersDialogOpen} onClose={() => setLinkedOrdersDialogOpen(false)}>
                <DialogTitle>{t("Orders")}</DialogTitle>
                <DialogContent>
                    {linkedOrders.length > 0 ? (
                        <ul style={{ paddingLeft: "1rem" }}>
                            {linkedOrders.map((order, index) => (
                                <li key={order.id ?? index}>
                                    <a
                                        href={`/orderdetailspage/${order.id}`}
                                        rel="noopener noreferrer"
                                        style={{ color: "#1976d2", textDecoration: "none" }}
                                    >
                                        {order.internalNumber}
                                    </a>
                                </li>
                            ))}
                        </ul>
                    ) : (
                        <p>{t("No linked orders found.")}</p>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setLinkedOrdersDialogOpen(false)}>
                        {t("Close")}
                    </Button>
                </DialogActions>
            </Dialog>

        </Layout>
    );
};

export default InvoiceListPage;
